import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { adminAPI } from '../../services/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../ui/accordion';
import { Save, Loader2, Palette, Check, Shuffle, Eye, Star, Trash2, Upload, Plus, Code } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import ThemePreview from './ThemePreview';

const ThemeEditor = () => {
  const { token } = useAuth();
  const { theme, updateTheme, refreshTheme } = useTheme();
  const { toast } = useToast();
  const [prebuiltThemes, setPrebuiltThemes] = useState([]);
  const [customThemes, setCustomThemes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedTheme, setSelectedTheme] = useState(null);
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
  const [jsonInput, setJsonInput] = useState('');
  const [jsonError, setJsonError] = useState('');
  const [importing, setImporting] = useState(false);
  const [customColors, setCustomColors] = useState({
    primary: '#ff6b35',
    secondary: '#f7931e',
    background: '#0a0a0a',
    surface: '#1a1a1a',
    text: '#ffffff',
    textSecondary: '#9ca3af',
    border: '#ff6b35'
  });

  const jsonTemplate = `{
  "name": "My Custom Theme",
  "colors": {
    "primary": "#ff6b35",
    "secondary": "#f7931e",
    "background": "#0a0a0a",
    "surface": "#1a1a1a",
    "text": "#ffffff",
    "textSecondary": "#9ca3af",
    "border": "#ff6b35"
  }
}`;

  useEffect(() => {
    fetchThemes();
    if (theme) {
      setCustomColors(theme.colors);
      setSelectedTheme(theme.isCustom ? 'custom' : findThemeId(theme));
    }
  }, [theme]);

  const findThemeId = (currentTheme) => {
    const match = prebuiltThemes.find(t => 
      JSON.stringify(t.colors) === JSON.stringify(currentTheme.colors)
    );
    return match ? match.id : 'custom';
  };

  const fetchThemes = async () => {
    try {
      setLoading(true);
      const [prebuiltRes, customRes] = await Promise.all([
        adminAPI.getPrebuiltThemes(token),
        adminAPI.getCustomThemes(token)
      ]);
      
      if (prebuiltRes.success) {
        setPrebuiltThemes(prebuiltRes.data.themes);
      }
      if (customRes.success) {
        setCustomThemes(customRes.data.themes || []);
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load themes',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleThemeSelect = (themeId, isCustom = false) => {
    setSelectedTheme(themeId);
    if (themeId !== 'custom') {
      const themeList = isCustom ? customThemes : prebuiltThemes;
      const selectedPrebuilt = themeList.find(t => t.id === themeId);
      if (selectedPrebuilt) {
        setCustomColors(selectedPrebuilt.colors);
        updateTheme({
          name: selectedPrebuilt.name,
          colors: selectedPrebuilt.colors,
          isCustom: isCustom
        });
      }
    }
  };

  const handleColorChange = (colorKey, value) => {
    const newColors = { ...customColors, [colorKey]: value };
    setCustomColors(newColors);
    setSelectedTheme('custom');
    updateTheme({
      name: 'Custom Theme',
      colors: newColors,
      isCustom: true
    });
  };

  const handleRandomize = () => {
    const allThemes = [...prebuiltThemes, ...customThemes];
    if (allThemes.length === 0) return;
    const randomIndex = Math.floor(Math.random() * allThemes.length);
    const randomTheme = allThemes[randomIndex];
    const isCustom = customThemes.some(t => t.id === randomTheme.id);
    handleThemeSelect(randomTheme.id, isCustom);
    toast({
      title: 'Random Theme Selected!',
      description: `Applied "${randomTheme.name}"`,
    });
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const allThemes = [...prebuiltThemes, ...customThemes];
      const themeData = {
        name: selectedTheme === 'custom' ? 'Custom Theme' : allThemes.find(t => t.id === selectedTheme)?.name || 'Custom Theme',
        colors: customColors,
        isCustom: selectedTheme === 'custom' || customThemes.some(t => t.id === selectedTheme)
      };

      const response = await adminAPI.updateTheme(themeData, token);
      if (response.success) {
        toast({
          title: 'Success!',
          description: 'Theme saved successfully',
        });
        await refreshTheme();
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to save theme',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleImportJSON = async () => {
    setJsonError('');
    try {
      const parsed = JSON.parse(jsonInput);
      
      if (!parsed.name || !parsed.colors) {
        setJsonError('JSON must contain "name" and "colors" fields');
        return;
      }

      const requiredColors = ['primary', 'secondary', 'background', 'surface', 'text', 'textSecondary', 'border'];
      for (const color of requiredColors) {
        if (!parsed.colors[color]) {
          setJsonError(`Missing required color: ${color}`);
          return;
        }
      }

      setImporting(true);
      const response = await adminAPI.importCustomTheme(parsed, token);
      
      if (response.success) {
        toast({
          title: 'Theme Imported!',
          description: `"${parsed.name}" has been added to your custom themes`,
        });
        setJsonInput('');
        await fetchThemes();
      }
    } catch (e) {
      if (e instanceof SyntaxError) {
        setJsonError('Invalid JSON format');
      } else {
        setJsonError(e.response?.data?.detail || 'Failed to import theme');
      }
    } finally {
      setImporting(false);
    }
  };

  const handleDeleteTheme = async (themeId) => {
    try {
      const response = await adminAPI.deleteCustomTheme(themeId, token);
      if (response.success) {
        toast({
          title: 'Theme Deleted',
          description: 'Custom theme has been removed',
        });
        await fetchThemes();
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete theme',
        variant: 'destructive',
      });
    }
  };

  const handleToggleFavorite = async (themeId) => {
    try {
      const response = await adminAPI.toggleThemeFavorite(themeId, token);
      if (response.success) {
        setCustomThemes(prev => 
          prev.map(t => t.id === themeId ? { ...t, isFavorite: response.data.isFavorite } : t)
        );
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update favorite',
        variant: 'destructive',
      });
    }
  };

  const handleSaveCurrentAsCustom = async () => {
    try {
      const name = prompt('Enter a name for your custom theme:');
      if (!name) return;

      const response = await adminAPI.createCustomTheme({
        name,
        colors: customColors
      }, token);

      if (response.success) {
        toast({
          title: 'Theme Saved!',
          description: `"${name}" has been added to your custom themes`,
        });
        await fetchThemes();
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to save custom theme',
        variant: 'destructive',
      });
    }
  };

  const filteredCustomThemes = showFavoritesOnly 
    ? customThemes.filter(t => t.isFavorite)
    : customThemes;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="animate-spin text-orange-500" size={40} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Theme Customization</h2>
          <p className="text-gray-400">Choose a pre-built theme, import from JSON, or create your own</p>
        </div>
        <div className="flex gap-3">
          <Button
            onClick={handleRandomize}
            variant="outline"
            className="border-orange-500/30 text-orange-500 hover:bg-orange-500/10"
          >
            <Shuffle className="mr-2" size={18} />
            Random
          </Button>
          <Button
            onClick={handleSaveCurrentAsCustom}
            variant="outline"
            className="border-blue-500/30 text-blue-500 hover:bg-blue-500/10"
          >
            <Plus className="mr-2" size={18} />
            Save as Custom
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white border-0"
          >
            {saving ? (
              <>
                <Loader2 className="mr-2 animate-spin" size={18} />
                Saving...
              </>
            ) : (
              <>
                <Save className="mr-2" size={18} />
                Apply Theme
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* Left Column: Theme Selection */}
        <div className="space-y-6">
          <Accordion type="multiple" defaultValue={["prebuilt", "custom"]} className="space-y-4">
            {/* Pre-built Themes */}
            <AccordionItem value="prebuilt" className="glass border border-orange-500/20 rounded-2xl px-6">
              <AccordionTrigger className="text-lg font-semibold text-white hover:text-orange-500">
                <div className="flex items-center gap-2">
                  <Palette size={20} className="text-orange-500" />
                  Pre-built Themes ({prebuiltThemes.length})
                </div>
              </AccordionTrigger>
              <AccordionContent className="pt-4">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 max-h-72 overflow-y-auto pr-2">
                  {prebuiltThemes.map((t) => (
                    <button
                      key={t.id}
                      onClick={() => handleThemeSelect(t.id, false)}
                      className={`p-3 rounded-xl border-2 transition-all duration-200 hover:scale-105 ${
                        selectedTheme === t.id
                          ? 'border-orange-500 bg-orange-500/10'
                          : 'border-gray-700 bg-zinc-900'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-white font-medium text-xs truncate">{t.name}</span>
                        {selectedTheme === t.id && (
                          <Check size={14} className="text-orange-500 flex-shrink-0" />
                        )}
                      </div>
                      <div className="flex gap-1 mb-1">
                        <div className="flex-1 h-5 rounded-md" style={{ backgroundColor: t.colors.primary }}></div>
                        <div className="flex-1 h-5 rounded-md" style={{ backgroundColor: t.colors.secondary }}></div>
                      </div>
                      <div className="flex gap-1">
                        <div className="flex-1 h-3 rounded-sm border border-gray-600" style={{ backgroundColor: t.colors.background }}></div>
                        <div className="flex-1 h-3 rounded-sm border border-gray-600" style={{ backgroundColor: t.colors.surface }}></div>
                      </div>
                    </button>
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* Custom Themes */}
            <AccordionItem value="custom" className="glass border border-blue-500/20 rounded-2xl px-6">
              <AccordionTrigger className="text-lg font-semibold text-white hover:text-blue-500">
                <div className="flex items-center gap-2">
                  <Star size={20} className="text-blue-500" />
                  My Custom Themes ({customThemes.length})
                </div>
              </AccordionTrigger>
              <AccordionContent className="pt-4">
                <div className="mb-3">
                  <Button
                    onClick={() => setShowFavoritesOnly(!showFavoritesOnly)}
                    variant="outline"
                    size="sm"
                    className={showFavoritesOnly ? 'border-yellow-500 text-yellow-500' : 'border-gray-600 text-gray-400'}
                  >
                    <Star size={14} className="mr-1" fill={showFavoritesOnly ? 'currentColor' : 'none'} />
                    Favorites Only
                  </Button>
                </div>
                {filteredCustomThemes.length > 0 ? (
                  <div className="grid grid-cols-2 gap-3 max-h-60 overflow-y-auto pr-2">
                    {filteredCustomThemes.map((t) => (
                      <div
                        key={t.id}
                        className={`p-3 rounded-xl border-2 transition-all duration-200 ${
                          selectedTheme === t.id
                            ? 'border-blue-500 bg-blue-500/10'
                            : 'border-gray-700 bg-zinc-900'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <button 
                            onClick={() => handleThemeSelect(t.id, true)}
                            className="text-white font-medium text-xs truncate flex-1 text-left"
                          >
                            {t.name}
                          </button>
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => handleToggleFavorite(t.id)}
                              className="p-1 hover:bg-yellow-500/20 rounded"
                            >
                              <Star size={14} className={t.isFavorite ? 'text-yellow-500 fill-yellow-500' : 'text-gray-500'} />
                            </button>
                            <button
                              onClick={() => handleDeleteTheme(t.id)}
                              className="p-1 hover:bg-red-500/20 rounded"
                            >
                              <Trash2 size={14} className="text-red-500" />
                            </button>
                          </div>
                        </div>
                        <button onClick={() => handleThemeSelect(t.id, true)} className="w-full">
                          <div className="flex gap-1 mb-1">
                            <div className="flex-1 h-5 rounded-md" style={{ backgroundColor: t.colors.primary }}></div>
                            <div className="flex-1 h-5 rounded-md" style={{ backgroundColor: t.colors.secondary }}></div>
                          </div>
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm text-center py-4">
                    {showFavoritesOnly ? 'No favorite themes yet' : 'No custom themes yet. Import or create one!'}
                  </p>
                )}
              </AccordionContent>
            </AccordionItem>

            {/* Import JSON */}
            <AccordionItem value="import" className="glass border border-green-500/20 rounded-2xl px-6">
              <AccordionTrigger className="text-lg font-semibold text-white hover:text-green-500">
                <div className="flex items-center gap-2">
                  <Code size={20} className="text-green-500" />
                  Import from JSON
                </div>
              </AccordionTrigger>
              <AccordionContent className="pt-4 space-y-4">
                <div>
                  <Label className="text-gray-300 mb-2 block">Paste your theme JSON below:</Label>
                  <Textarea
                    value={jsonInput}
                    onChange={(e) => {
                      setJsonInput(e.target.value);
                      setJsonError('');
                    }}
                    placeholder={jsonTemplate}
                    rows={10}
                    className="bg-zinc-900 border-green-500/20 text-white font-mono text-sm"
                  />
                  {jsonError && (
                    <p className="text-red-500 text-sm mt-2">{jsonError}</p>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={handleImportJSON}
                    disabled={importing || !jsonInput.trim()}
                    className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white border-0"
                  >
                    {importing ? (
                      <>
                        <Loader2 className="mr-2 animate-spin" size={18} />
                        Importing...
                      </>
                    ) : (
                      <>
                        <Upload className="mr-2" size={18} />
                        Import Theme
                      </>
                    )}
                  </Button>
                  <Button
                    onClick={() => setJsonInput(jsonTemplate)}
                    variant="outline"
                    className="border-gray-600 text-gray-400 hover:bg-gray-800"
                  >
                    Load Template
                  </Button>
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* Custom Colors Editor */}
            <AccordionItem value="colors" className="glass border border-purple-500/20 rounded-2xl px-6">
              <AccordionTrigger className="text-lg font-semibold text-white hover:text-purple-500">
                <div className="flex items-center gap-2">
                  <Palette size={20} className="text-purple-500" />
                  Custom Colors Editor
                </div>
              </AccordionTrigger>
              <AccordionContent className="pt-4">
                <div className="grid grid-cols-1 gap-3">
                  {Object.entries(customColors).map(([key, value]) => (
                    <div key={key} className="flex items-center gap-3">
                      <Label className="text-gray-300 capitalize w-32 text-sm">
                        {key.replace(/([A-Z])/g, ' $1').trim()}
                      </Label>
                      <Input
                        type="text"
                        value={value || '#000000'}
                        onChange={(e) => handleColorChange(key, e.target.value)}
                        className="bg-zinc-900 border-purple-500/20 text-white flex-1 text-sm"
                        placeholder="#000000"
                      />
                      <input
                        type="color"
                        value={value || '#000000'}
                        onChange={(e) => handleColorChange(key, e.target.value)}
                        className="w-10 h-8 rounded cursor-pointer bg-transparent border-2 border-gray-600"
                      />
                    </div>
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>

        {/* Right Column: Live Preview */}
        <div className="space-y-6">
          <div className="glass p-6 rounded-3xl border border-orange-500/20 sticky top-6">
            <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <Eye size={24} className="text-orange-500" />
              Live Preview
            </h3>
            <div className="max-h-[calc(100vh-200px)] overflow-y-auto pr-2">
              <ThemePreview colors={customColors} />
            </div>
          </div>
        </div>
      </div>

      {/* Info Note */}
      <div className="glass p-4 rounded-xl border border-blue-500/20 bg-blue-500/5">
        <p className="text-blue-400 text-sm">
          💡 <strong>Tip:</strong> Click "Save as Custom" to save your current color scheme. Use "Apply Theme" to apply changes to your portfolio.
        </p>
      </div>
    </div>
  );
};

export default ThemeEditor;
